import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '@shared/components/toast/services/toast.service';
import {
  REQUEST_FAILED_FALLBACK_KEY,
  SUPPRESS_ERROR_TOAST,
  extractErrorMessage,
} from '@core/http/api-response.envelope';

/**
 * Surface error responses as toasts using the localized message the server
 * returns.
 *
 * Because the backend honours `Accept-Language`, `error.error.message` is
 * already in the active app language and is rendered verbatim. If the body
 * is empty (network failure, generic 5xx, etc.) we fall back to a
 * translated `common.errors.requestFailed` string.
 *
 * Per-request opt-out: `withSuppressErrorToast()` — useful when a screen
 * wants to render the error inline (e.g. as a form-level message) instead.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const translate = inject(TranslateService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!req.context.get(SUPPRESS_ERROR_TOAST)) {
        const fallback = translate.instant(REQUEST_FAILED_FALLBACK_KEY);
        const message = extractErrorMessage(error, fallback);
        toastService.error(message);
      }
      return throwError(() => error);
    })
  );
};
